<template>
  <Teleport v-if="initialGalleryInfo.mode === 'default'" to="body">
    <Gallery
      v-if="modalOpen"
      :payload="store.selectedGallery"
      :initialMediaIndex="initialMediaIndex"
      :labelUGC="labelUGC"
      @close="onClose"
    />
  </Teleport>

  <Teleport
    v-if="initialGalleryInfo.mode === 'embedded' && initialGalleryInfo.element"
    :to="initialGalleryInfo.element"
  >
    <GalleryViewer
      v-if="modalOpen && store.selectedGallery?.allMedia"
      :medias="store.selectedGallery?.allMedia"
      :mediaIndexToSelect="initialMediaIndex"
    />
  </Teleport>
</template>

<script setup>
import useEventListener from '@/composables/useEventListener'
import { useFetchGallery, useFetchGalleryFromGlobal } from '@/composables/useGallery'
import { getQueryParams } from '@/composables/UseUrl'
import { reactive, ref, watch, onMounted, onUnmounted, computed } from 'vue'
import { Gallery } from 'rhg-components-template'
import { GalleryViewer } from 'rhg-components-template'
import { Gallery as GalleryEntity } from 'rhg-components-template/entities'
import 'rhg-components-template/css'
import { useTranslate } from '@common/lib-vue-common'

const isLoading = ref(0)
const modalOpen = ref(false)
const preloadedGalleries = ref(false)
const dataGalleryTitle = ref('')
const initialMediaIndex = ref(null)
const initialGalleryInfo = reactive({
  mode: 'default',
  element: null,
  subGalleryId: null,
  mediaIndex: null,
  mediaId: null
})
const store = reactive({
  selectedGallery: {},
  galleries: []
})
const previousUrl = ref(null)

watch(modalOpen, newValue => {
  if (!newValue) store.selectedGallery = null
})

const clickEvent = useEventListener('click')
const clickDestroyEmbeddedGalleryViewerEvent = useEventListener('destoy-embedded-gallery-viewer')

const popstateHandler = () => {
  const paramGalleryId = getQueryParams(window.location.search, 'galleryId')
  if (!paramGalleryId) {
    onClose()
  }
}

onMounted(() => {
  initialGalleryInfo.mediaId = getQueryParams(window.location.search, 'mediaId') || 0
  window.addEventListener('popstate', popstateHandler, { passive: true })

  const paramGalleryId = getQueryParams(window.location.search, 'galleryId')
  if (paramGalleryId) loadGallery(paramGalleryId)
})

onUnmounted(() => {
  window.removeEventListener('popstate', popstateHandler)
})

const labelUGC = computed(() => useTranslate('weGalleryFromOurGuests'))

const setModalStatus = value => {
  modalOpen.value = value
}

const setLoading = value => {
  value ? (isLoading.value += 1) : (isLoading.value -= 1)
}

const getMediaIndexFromMediaId = gallery => {
  return gallery.allMedia.findIndex(media => media.id === initialGalleryInfo.mediaId)
}

const getMediaIndexFromSubGalleryId = gallery => {
  if (!initialGalleryInfo.subGalleryId || initialGalleryInfo.mediaIndex > 0)
    return initialGalleryInfo.mediaIndex

  return gallery.subGalleries.reduce(
    (acc, curr) => {
      if (acc.foundRoom === true || curr.id === initialGalleryInfo.subGalleryId) {
        return { count: acc.count, foundRoom: true }
      }
      return { ...acc, count: acc.count + curr.medias.length }
    },
    { count: 0, foundRoom: false }
  ).count
}

watch(clickEvent, async newEvent => {
  const triggerDataAttr = newEvent.target.dataset
  if (!triggerDataAttr.galleryType) return

  newEvent.preventDefault()

  if (triggerDataAttr.galleryMode === 'embedded' && triggerDataAttr.galleryElement) {
    initialGalleryInfo.mode = triggerDataAttr.galleryMode
    initialGalleryInfo.element = document.querySelector(triggerDataAttr.galleryElement)
  } else {
    initialGalleryInfo.mode = 'default'
    initialGalleryInfo.element = null
  }

  initialGalleryInfo.mediaId = null
  initialGalleryInfo.subGalleryId = triggerDataAttr.subgalleryId || null
  initialGalleryInfo.mediaIndex = triggerDataAttr.slide || null
  initialGalleryInfo.mediaId = triggerDataAttr.mediaId || null
  dataGalleryTitle.value = triggerDataAttr.galleryTitle
  const targetGalleryId = triggerDataAttr.galleryType + '|' + triggerDataAttr.galleryTypeId
  const fullPath =
    typeof window.fullPath !== 'undefined' && window.fullPath
      ? window.fullPath
      : window.location.pathname + window.location.search + window.location.hash
  if (fullPath) {
    previousUrl.value = window.location.href
    window.history.pushState({}, '', fullPath)
  }
  console.group('Gallery Click')
  console.log('Trigger data attributes', triggerDataAttr)
  console.log('Created gallery ID', targetGalleryId)
  console.groupEnd()

  targetGalleryId && loadGallery(targetGalleryId)
})

watch(clickDestroyEmbeddedGalleryViewerEvent, () => {
  onClose()
})

const loadGallery = async targetGalleryId => {
  await preloadGalleries()
  setModalStatus(true)
  const selectedGallery = store.galleries.find(gallery => gallery.id === targetGalleryId)
  if (!selectedGallery) {
    setLoading(true)
    const requestedGalleries = await useFetchGallery(targetGalleryId, dataGalleryTitle.value)
    setLoading(false)
    if (requestedGalleries.length > 0) {
      store.galleries = [...store.galleries, ...requestedGalleries]
    } else {
      setModalStatus(false)
    }
  }

  const foundGallery = store.galleries.find(gallery => gallery.id === targetGalleryId)
  initialMediaIndex.value = initialGalleryInfo.mediaId
    ? getMediaIndexFromMediaId(foundGallery)
    : getMediaIndexFromSubGalleryId(foundGallery)

  if (foundGallery instanceof GalleryEntity) {
    setModalStatus(true)
    store.selectedGallery = foundGallery
  } else {
    setModalStatus(false)
    store.selectedGallery = null
  }
  console.log('selected gallery', store.galleries, store.selectedGallery)
  window.pageGalleries = store.galleries
  window.selectedGallery = store.selectedGallery
}

const preloadGalleries = async () => {
  if (preloadedGalleries.value) return

  try {
    const preloadedGalleries = await useFetchGalleryFromGlobal()
    store.galleries = [...store.galleries, ...preloadedGalleries]
  } catch (error) {
    console.error(error)
  }

  preloadedGalleries.value = true
}

const onClose = () => {
  console.log('* closing modal')
  modalOpen.value = false
  store.selectedGallery = null
  if (previousUrl.value) {
    window.history.replaceState({}, '', previousUrl.value)
    previousUrl.value = null
  }
}
</script>

<style>
[data-gallery-type] {
  cursor: pointer;
}

[data-gallery-type] * {
  pointer-events: none;
}

.hero .jw-media {
  pointer-events: none;
}
</style>
